import { Platform } from 'react-native';
import type { FeatureFlag } from '@/features/feature-flags';

/**
 * Registry visibility gating (pure, side-effect free).
 *
 * Two orthogonal levers decide whether an entry is *shown*:
 *  - `platform`: the entry is limited to a subset of platforms.
 *  - `flag`: the entry sits behind a feature flag (a build gate; see
 *    CONTEXT.md). Flag names are typed, so a typo is a compile error.
 *
 * Both default to "visible": an entry with neither field renders everywhere.
 * A third lever, `dependsOnPermission`, *disables* (does not hide) a row and is
 * resolved at the row level because it needs the permission hook — see
 * `registry-row.tsx`.
 */
export type PlatformOs = 'web' | 'ios' | 'android';

/** A flag-name to value map. Partial so callers may pass only what they know. */
export type FlagMap = Partial<Record<FeatureFlag, boolean>>;

/** The gate-relevant slice of a registry entry. */
export interface GatingFields {
  platform?: readonly PlatformOs[];
  flag?: FeatureFlag;
}

/** Map React Native's broader `Platform.OS` union onto our three targets. */
function currentPlatform(): PlatformOs {
  return Platform.OS === 'ios' || Platform.OS === 'android' ? Platform.OS : 'web';
}

export function isPlatformAllowed(
  entry: GatingFields,
  os: PlatformOs = currentPlatform(),
): boolean {
  if (!entry.platform || entry.platform.length === 0) return true;
  return entry.platform.includes(os);
}

export function isFlagEnabled(
  entry: GatingFields,
  flags?: FlagMap,
): boolean {
  if (!entry.flag) return true;
  // Unknown flags default to enabled; only an explicit `false` hides the entry.
  return flags?.[entry.flag] !== false;
}

export interface VisibilityOptions {
  os?: PlatformOs;
  flags?: FlagMap;
}

export function isEntryVisible(
  entry: GatingFields,
  opts: VisibilityOptions = {},
): boolean {
  return (
    isPlatformAllowed(entry, opts.os) && isFlagEnabled(entry, opts.flags)
  );
}
